import { Alert, AlertTitle, Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { Handshake } from '@icons/completed/Handshake';
import { useV2Page } from '../../Context/V2PageContext';

/**
 * Renders after the BE has returned `done: true` from a page submission (or
 * from the initial bootstrap if the case is already complete).
 *
 * Two pieces of copy come from the BE:
 *   - `thankYouMessage`: the success line
 *   - `nextStepInstructions`: optional follow-up guidance
 *
 * Both are optional — the screen still renders sensibly with neither, so a
 * minimal BE response (just `done: true`) still produces a polished view.
 *
 * The completion icon is the same `Handshake` SVG used by the legacy v1
 * `Completed` screen (see Components/Completed.tsx) so HRR completion is
 * visually identical to v1.
 */
export function ThankYouScreen() {
	const { completion } = useV2Page();
	const theme = useTheme();

	return (
		<Stack
			spacing={3}
			sx={{ maxWidth: 640, mx: 'auto', p: 3 }}
			data-cy="hrr-thankyou-screen">
			<Box textAlign="center">
				<Typography variant="h4" gutterBottom data-cy="hrr-thankyou-title">
					Thank you
				</Typography>
				<Typography variant="body1" color="text.secondary" data-cy="hrr-thankyou-message">
					{completion?.thankYouMessage ??
						'Your answers have been submitted. You can close this window now.'}
				</Typography>
				<Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }} data-cy="hrr-thankyou-icon">
					<Handshake
						primaryColor={theme.palette.primary.main}
						secondaryColor={theme.palette.secondary.main}
					/>
				</Box>
			</Box>

			{completion?.nextStepInstructions && (
				<Paper variant="outlined" sx={{ p: 2 }} data-cy="hrr-thankyou-next-steps">
					<Alert severity="info" icon={false}>
						<AlertTitle>What happens next</AlertTitle>
						{completion.nextStepInstructions}
					</Alert>
				</Paper>
			)}
		</Stack>
	);
}
